import React, { useState } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Badge } from '../ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Alert, AlertDescription } from '../ui/alert'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'
import { Separator } from '../ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { Switch } from '../ui/switch'
import { Label } from '../ui/label'
import { Textarea } from '../ui/textarea'
import { 
  ArrowLeft,
  Users, 
  Globe,
  Settings,
  Download,
  Upload,
  Trash2,
  Copy,
  Save,
  UserPlus,
  Shield,
  Eye,
  Edit,
  Bell,
  Lock,
  Key,
  Palette,
  Type,
  Image,
  FileText,
  CreditCard,
  Activity,
  MessageSquare,
  Mail
} from 'lucide-react'
import { TeamManagement } from './TeamManagement'
import { ActivityFeed } from './ActivityFeed'
import { Comments } from './Comments'
import { PublicBrandPage } from './PublicBrandPage'

interface Brand {
  id: string
  name: string
  description?: string
  website_url?: string
  is_public?: boolean
  slug?: string
  metadata?: {
    industry?: string
  }
}

interface BrandSettingsProps {
  brand: Brand
  onBack: () => void
}

export function BrandSettings({ brand, onBack }: BrandSettingsProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [showPreview, setShowPreview] = useState(false)
  const [brandData, setBrandData] = useState({
    name: brand.name,
    description: brand.description || '',
    website_url: brand.website_url || '',
    industry: brand.metadata?.industry || ''
  })

  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    teamUpdates: true,
    aiSuggestions: true,
    publicUpdates: false,
    autoSave: true,
    darkMode: false
  })

  const handleSaveGeneral = async () => {
    setIsLoading(true)
    // TODO: Implement save functionality
    setTimeout(() => {
      setIsLoading(false)
    }, 1000)
  }

  const handleSavePreferences = async () => {
    setIsLoading(true)
    // TODO: Implement save functionality
    setTimeout(() => {
      setIsLoading(false)
    }, 1000)
  }

  const handlePublishBrand = () => {
    // TODO: Implement brand publishing
    console.log('Publish brand')
  }

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(`https://brandzit.com/brand/${brand.slug}`)
  }

  const handleDeleteBrand = () => {
    // TODO: Implement brand deletion with confirmation
    console.log('Delete brand')
  }

  const handlePreviewPublicPage = () => {
    setShowPreview(true)
  }

  const handleClosePreview = () => {
    setShowPreview(false)
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={onBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div className="h-6 w-px bg-slate-300" />
              <div className="flex items-center space-x-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src="" alt={brand.name} />
                  <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white font-semibold">
                    {brand.name.substring(0, 2).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-xl font-bold">{brand.name} Settings</h1>
                    {brand.is_public && (
                      <Badge variant="secondary" className="bg-green-100 text-green-700">
                        <Globe className="w-3 h-3 mr-1" />
                        Published
                      </Badge>
                    )}
                  </div>
                  {brand.metadata?.industry && (
                    <p className="text-xs text-slate-500">{brand.metadata.industry}</p>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button 
                size="sm" 
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                onClick={handlePublishBrand}
              >
                <Globe className="w-4 h-4 mr-2" />
                {brand.is_public ? 'Update' : 'Publish'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="general" className="space-y-6">
          <TabsList className="grid w-full grid-cols-6 max-w-2xl">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="collaboration">Team</TabsTrigger>
            <TabsTrigger value="public">Public Page</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
          </TabsList>

          {/* General Settings */}
          <TabsContent value="general" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Brand Information</CardTitle>
                    <CardDescription>
                      Update your brand's basic information and metadata
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="brand-name">Brand Name</Label>
                        <Input 
                          id="brand-name"
                          value={brandData.name}
                          onChange={(e) => setBrandData({...brandData, name: e.target.value})}
                          placeholder="Enter brand name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="industry">Industry</Label>
                        <Input 
                          id="industry"
                          value={brandData.industry}
                          onChange={(e) => setBrandData({...brandData, industry: e.target.value})}
                          placeholder="e.g. Technology, Healthcare"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Description</Label>
                      <Textarea
                        id="description"
                        value={brandData.description}
                        onChange={(e) => setBrandData({...brandData, description: e.target.value})}
                        placeholder="Brief description of your brand"
                        rows={3}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="website">Website URL</Label>
                      <Input 
                        id="website"
                        value={brandData.website_url}
                        onChange={(e) => setBrandData({...brandData, website_url: e.target.value})}
                        placeholder="https://example.com"
                      />
                    </div>

                    <div className="flex items-center space-x-2 pt-4">
                      <Button onClick={handleSaveGeneral} disabled={isLoading}>
                        <Save className="w-4 h-4 mr-2" />
                        {isLoading ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button variant="outline" onClick={() => setBrandData({
                        name: brand.name,
                        description: brand.description || '',
                        website_url: brand.website_url || '',
                        industry: brand.metadata?.industry || ''
                      })}>
                        Reset
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Brand Assets</CardTitle>
                    <CardDescription>
                      Manage your brand's visual assets and files
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Button variant="outline" className="h-24 flex-col space-y-2">
                        <Upload className="w-6 h-6" />
                        <span>Upload Logo</span>
                      </Button>
                      <Button variant="outline" className="h-24 flex-col space-y-2">
                        <Palette className="w-6 h-6" />
                        <span>Brand Colors</span>
                      </Button>
                      <Button variant="outline" className="h-24 flex-col space-y-2">
                        <Type className="w-6 h-6" />
                        <span>Typography</span>
                      </Button>
                      <Button variant="outline" className="h-24 flex-col space-y-2">
                        <Image className="w-6 h-6" />
                        <span>Images</span>
                      </Button>
                    </div>
                    
                    <Alert>
                      <AlertDescription>
                        Upload your brand assets to automatically populate guidelines and improve AI generation accuracy.
                      </AlertDescription>
                    </Alert>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button variant="outline" className="w-full justify-start">
                      <Download className="w-4 h-4 mr-2" />
                      Export Guidelines
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate Brand
                    </Button>
                    <Button variant="outline" className="w-full justify-start" onClick={handlePreviewPublicPage}>
                      <Eye className="w-4 h-4 mr-2" />
                      Preview Public Page
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <FileText className="w-4 h-4 mr-2" />
                      Generate Report
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Usage Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">AI Assets Generated</span>
                      <span className="font-medium">24</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Team Members</span>
                      <span className="font-medium">3</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Page Views</span>
                      <span className="font-medium">147</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Downloads</span>
                      <span className="font-medium">12</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Collaboration Settings */}
          <TabsContent value="collaboration" className="space-y-6">
            <Tabs defaultValue="team" className="space-y-6">
              <TabsList className="grid w-full grid-cols-3 max-w-md">
                <TabsTrigger value="team">Team Members</TabsTrigger>
                <TabsTrigger value="activity">Activity Feed</TabsTrigger>
                <TabsTrigger value="comments">Comments</TabsTrigger>
              </TabsList>

              <TabsContent value="team">
                <TeamManagement brandId={brand.id} />
              </TabsContent>

              <TabsContent value="activity">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Team Activity
                    </CardTitle>
                    <CardDescription>
                      Track all team activities and changes to this brand
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ActivityFeed brandId={brand.id} limit={20} />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="comments">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Brand Comments & Feedback
                    </CardTitle>
                    <CardDescription>
                      View and respond to team comments about this brand
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Comments 
                      brandId={brand.id}
                      targetType="brand"
                      targetId={brand.id}
                      title="Brand Feedback"
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Public Page Settings */}
          <TabsContent value="public" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Public Brand Page</CardTitle>
                    <CardDescription>
                      Configure your public-facing brand guidelines page
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">Public Access</p>
                        <p className="text-sm text-slate-600">Allow anyone to view your brand guidelines</p>
                      </div>
                      <Switch 
                        checked={brand.is_public}
                        onCheckedChange={() => {/* TODO: toggle public status */}}
                      />
                    </div>

                    {brand.is_public && (
                      <div className="space-y-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="space-y-2">
                          <Label htmlFor="public-url">Public URL</Label>
                          <div className="flex items-center gap-2">
                            <Input 
                              id="public-url"
                              value={`https://brandzit.com/brand/${brand.slug || 'your-brand'}`} 
                              readOnly 
                              className="bg-white"
                            />
                            <Button variant="outline" size="sm" onClick={handleCopyUrl}>
                              <Copy className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Button variant="outline" size="sm" onClick={handlePreviewPublicPage}>
                            <Eye className="w-4 h-4 mr-2" />
                            Preview Page
                          </Button>
                          <Button variant="outline" size="sm">
                            <Download className="w-4 h-4 mr-2" />
                            Download Guidelines
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="page-title">Page Title</Label>
                        <Input 
                          id="page-title"
                          defaultValue={`${brand.name} Brand Guidelines`} 
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="page-description">Description</Label>
                        <Input 
                          id="page-description"
                          defaultValue="Official brand guidelines and assets" 
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="custom-slug">Custom Slug</Label>
                        <div className="flex items-center">
                          <span className="text-sm text-slate-600 bg-slate-100 px-3 py-2 rounded-l border">brandzit.com/brand/</span>
                          <Input 
                            id="custom-slug"
                            defaultValue={brand.slug || 'your-brand'} 
                            className="rounded-l-none"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button onClick={handlePublishBrand}>
                        <Globe className="w-4 h-4 mr-2" />
                        {brand.is_public ? 'Update Public Page' : 'Publish Brand Page'}
                      </Button>
                      {brand.is_public && (
                        <Button variant="outline">
                          Unpublish
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Page Analytics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Total Views</span>
                      <span className="font-medium">1,247</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">This Month</span>
                      <span className="font-medium">89</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Downloads</span>
                      <span className="font-medium">23</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Unique Visitors</span>
                      <span className="font-medium">156</span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>SEO Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="meta-description">Meta Description</Label>
                      <Textarea
                        id="meta-description"
                        placeholder="Brief description for search engines"
                        rows={3}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="keywords">Keywords</Label>
                      <Input 
                        id="keywords"
                        placeholder="brand, design, guidelines"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* Preferences Settings */}
          <TabsContent value="preferences" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Notifications
                  </CardTitle>
                  <CardDescription>
                    Configure how you receive updates about this brand
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Email Notifications</p>
                      <p className="text-sm text-slate-600">Receive email updates about brand changes</p>
                    </div>
                    <Switch 
                      checked={preferences.emailNotifications}
                      onCheckedChange={(checked) => setPreferences({...preferences, emailNotifications: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Team Updates</p>
                      <p className="text-sm text-slate-600">Get notified when team members make changes</p>
                    </div>
                    <Switch 
                      checked={preferences.teamUpdates}
                      onCheckedChange={(checked) => setPreferences({...preferences, teamUpdates: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">AI Suggestions</p>
                      <p className="text-sm text-slate-600">Receive AI-powered brand improvement suggestions</p>
                    </div>
                    <Switch 
                      checked={preferences.aiSuggestions}
                      onCheckedChange={(checked) => setPreferences({...preferences, aiSuggestions: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Public Page Updates</p>
                      <p className="text-sm text-slate-600">Notify when public brand page is accessed</p>
                    </div>
                    <Switch 
                      checked={preferences.publicUpdates}
                      onCheckedChange={(checked) => setPreferences({...preferences, publicUpdates: checked})}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Editor Preferences
                  </CardTitle>
                  <CardDescription>
                    Customize your editing experience
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Auto Save</p>
                      <p className="text-sm text-slate-600">Automatically save changes as you work</p>
                    </div>
                    <Switch 
                      checked={preferences.autoSave}
                      onCheckedChange={(checked) => setPreferences({...preferences, autoSave: checked})}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Dark Mode</p>
                      <p className="text-sm text-slate-600">Use dark theme for the interface</p>
                    </div>
                    <Switch 
                      checked={preferences.darkMode}
                      onCheckedChange={(checked) => setPreferences({...preferences, darkMode: checked})}
                    />
                  </div>

                  <div className="pt-4">
                    <Button onClick={handleSavePreferences} disabled={isLoading}>
                      <Save className="w-4 h-4 mr-2" />
                      Save Preferences
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Security Settings */}
          <TabsContent value="security" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Access Control
                  </CardTitle>
                  <CardDescription>
                    Manage who can access and modify this brand
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Password Protection</p>
                        <p className="text-sm text-slate-600">Require password for public page access</p>
                      </div>
                      <Switch />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">IP Restrictions</p>
                        <p className="text-sm text-slate-600">Limit access to specific IP addresses</p>
                      </div>
                      <Switch />
                    </div>

                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">Download Protection</p>
                        <p className="text-sm text-slate-600">Require login to download assets</p>
                      </div>
                      <Switch />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5" />
                    API Access
                  </CardTitle>
                  <CardDescription>
                    Generate API keys for integrations
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <AlertDescription>
                      API access is available on Pro and Enterprise plans.
                    </AlertDescription>
                  </Alert>
                  
                  <Button variant="outline" className="w-full" disabled>
                    <Key className="w-4 h-4 mr-2" />
                    Generate API Key
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Advanced Settings */}
          <TabsContent value="advanced" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Data Export</CardTitle>
                    <CardDescription>
                      Export your brand data and guidelines in various formats
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <Button variant="outline" className="justify-start">
                        <Download className="w-4 h-4 mr-2" />
                        Export as PDF
                      </Button>
                      <Button variant="outline" className="justify-start">
                        <Download className="w-4 h-4 mr-2" />
                        Export as JSON
                      </Button>
                      <Button variant="outline" className="justify-start">
                        <Download className="w-4 h-4 mr-2" />
                        Download Assets
                      </Button>
                      <Button variant="outline" className="justify-start">
                        <Download className="w-4 h-4 mr-2" />
                        Style Guide PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Data Import</CardTitle>
                    <CardDescription>
                      Import brand assets and data from external sources
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button variant="outline" className="w-full justify-start">
                      <Upload className="w-4 h-4 mr-2" />
                      Import Brand Assets
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Upload className="w-4 h-4 mr-2" />
                      Import from Figma
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <Upload className="w-4 h-4 mr-2" />
                      Import Color Palette
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-red-200">
                  <CardHeader>
                    <CardTitle className="text-red-700">Danger Zone</CardTitle>
                    <CardDescription>
                      Irreversible actions that will permanently affect your brand
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Alert>
                      <AlertDescription>
                        <strong>Warning:</strong> These actions cannot be undone. Please proceed with caution.
                      </AlertDescription>
                    </Alert>
                    
                    <div className="space-y-3">
                      <Button variant="outline" className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete All AI Assets
                      </Button>
                      <Button variant="outline" className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Reset Brand Progress
                      </Button>
                      <Button variant="outline" className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50" onClick={handleDeleteBrand}>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Brand Permanently
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Billing Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Current Plan</span>
                      <span className="font-medium">Pro</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Billing Cycle</span>
                      <span className="font-medium">Monthly</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Next Payment</span>
                      <span className="font-medium">Jan 15</span>
                    </div>
                    <Button variant="outline" className="w-full">
                      <CreditCard className="w-4 h-4 mr-2" />
                      Manage Billing
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Support</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button variant="outline" className="w-full justify-start">
                      <Mail className="w-4 h-4 mr-2" />
                      Contact Support
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <FileText className="w-4 h-4 mr-2" />
                      Documentation
                    </Button>
                    <Button variant="outline" className="w-full justify-start">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Feature Request
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Public Page Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-6xl h-[90vh] overflow-hidden">
            <div className="h-full overflow-y-auto">
              <PublicBrandPage 
                brand={brand} 
                onBack={handleClosePreview}
                isPreview={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}